import React, { useEffect, useMemo, useState, useRef, lazy, Component } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ShoppingCart, Phone, Mail, Check, ChevronLeft, ChevronRight, MessageSquare, Info, ChevronDown, FileText, Download, Truck, Calculator as CalcIcon, X } from 'lucide-react';
import { categories } from '../data/products';
import { useCart } from '../components/CartContext';
import { QuoteModal } from '../components/QuoteModal';
import { SameDayBadge } from '../components/SameDayBadge';
import { generateSpecSheet } from '../utils/generateSpecSheet';
import { buildDeliveryLineItems, getDefaultQuantity } from '../utils/loadSplit';
import { CartHelper } from '../components/CartHelper';
import { SimpleAreaCalculator } from '../components/SimpleAreaCalculator';
function CustomSelect({
  value,
  onChange,
  options,
  placeholder,
  className = ''
}: {
  value: string;
  onChange: (val: string) => void;
  options: {
    value: string;
    label: string;
    disabled?: boolean;
  }[];
  placeholder: string;
  className?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const selectedOption = options.find(opt => opt.value === value);
  return <div className={`relative ${className}`} ref={dropdownRef} data-id="element-2672">
      <button type="button" onClick={() => setIsOpen(!isOpen)} className={`w-full px-4 py-3 bg-[#e8ede4] dark:bg-emerald-500/[0.07] border border-[#d4ddcf] dark:border-emerald-500/20 rounded-none text-brand-charcoal dark:text-white focus:outline-none focus:ring-2 focus:ring-[#5fb478] focus:border-transparent transition-all flex items-center justify-between font-medium hover:bg-[#dde5d6] dark:hover:bg-emerald-500/[0.12] ${value ? '' : 'text-stone-500'}`} data-id="element-2673">
        <span className="truncate" data-id="element-2674">
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 ml-2 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-2675" />
      </button>

      <AnimatePresence data-id="element-2676">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} transition={{
        duration: 0.15
      }} className="absolute z-50 w-full top-full mt-2 bg-white dark:bg-stone-900 border border-[#d4ddcf] dark:border-white/10 rounded-none shadow-xl overflow-hidden max-h-60 overflow-y-auto" data-id="element-2677">
            {options.map(opt => <button key={opt.value} type="button" disabled={opt.disabled} onClick={() => {
          if (!opt.disabled) {
            onChange(opt.value);
            setIsOpen(false);
          }
        }} className={`w-full text-left px-4 py-3 text-sm transition-colors ${opt.disabled ? 'text-stone-300 dark:text-white/30 cursor-not-allowed bg-stone-50 dark:bg-stone-900/50' : value === opt.value ? 'bg-[#e3f7e8] dark:bg-[#0f3e1e] text-[#1a7a3a] dark:text-[#a8e8b8] font-semibold' : 'text-brand-charcoal dark:text-white hover:bg-stone-50 dark:hover:bg-white/10'}`} data-id="element-2678">
                {opt.label}
              </button>)}
          </motion.div>}
      </AnimatePresence>
    </div>;
}
const ProductImageCarousel = ({
  images,
  alt
}: {
  images: string[];
  alt: string;
}) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    setIdx(0);
  }, [images]);
  if (!images || images.length <= 1) {
    return <img src={images[0]} alt={alt} className="absolute inset-0 w-full h-full object-cover" data-id="element-2679" />;
  }
  return <div className="absolute inset-0 w-full h-full group" data-id="element-2680">
      <img src={images[idx]} alt={alt} className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300" data-id="element-2681" />
      <div className="absolute inset-0 flex items-center justify-between p-4 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-2682">
        <button onClick={e => {
        e.stopPropagation();
        setIdx(i => i === 0 ? images.length - 1 : i - 1);
      }} className="bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors" data-id="element-2683">
          <ChevronLeft className="w-5 h-5" data-id="element-2684" />
        </button>
        <button onClick={e => {
        e.stopPropagation();
        setIdx(i => i === images.length - 1 ? 0 : i + 1);
      }} className="bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors" data-id="element-2685">
          <ChevronRight className="w-5 h-5" data-id="element-2686" />
        </button>
      </div>
      <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2" data-id="element-2687">
        {images.map((_, i) => <button key={i} onClick={e => {
        e.stopPropagation();
        setIdx(i);
      }} className={`w-2 h-2 rounded-full transition-colors ${i === idx ? 'bg-white' : 'bg-white/50 hover:bg-white/75'}`} data-id="element-2688" />)}
      </div>
    </div>;
};
export function Product() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    addToCart
  } = useCart();
  // Lazy-init the default quantity per product. Soil/Mulch/Org. products
  // (topsoil, bark, compost, play chips, fill dirt) default to a 15-yd
  // light load — that's the typical homeowner spring-project size and
  // the size of one full light-material truckload. The 50/50 Mix is
  // the explicit exception (defaults to 12 — see getDefaultQuantity in
  // utils/loadSplit.ts). Everything else (rock, drain, quarry, decor)
  // defaults to a 12-yd heavy load.
  const [quantity, setQuantity] = useState<string>(() => {
    for (const cat of categories) {
      const found = cat.products.find(p => p.slug === slug);
      if (found) return getDefaultQuantity(found.name);
    }
    return '12';
  });
  const [numLoads, setNumLoads] = useState<string>('');
  const [addedFlash, setAddedFlash] = useState(false);
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'specs' | 'docs'>('overview');
  // Custom yardage handed off from the Instant Estimator via ?qty=N (e.g. 17).
  // When set, the page bypasses the standard dropdown and adds-to-cart using
  // the exact estimator yardage via buildDeliveryLineItems, so the 5yd-per-
  // load minimum and the delivery split match the estimator's quote exactly.
  // Clearing it (via the "Change quantity" link) reverts to the dropdown.
  const [estimatorQty, setEstimatorQty] = useState<number | null>(null);
  // Calculator Drawer State
  const [isCalcOpen, setIsCalcOpen] = useState(false);
  // Build flat list of all products for prev/next navigation
  const allProducts = useMemo(() => {
    const list: {
      slug: string;
      name: string;
      categoryName: string;
    }[] = [];
    for (const cat of categories) {
      for (const prod of cat.products) {
        list.push({
          slug: prod.slug,
          name: prod.name,
          categoryName: cat.name
        });
      }
    }
    return list;
  }, []);
  // Find product and its category
  let product = null;
  let category = null;
  for (const cat of categories) {
    const found = cat.products.find(p => p.slug === slug);
    if (found) {
      product = found;
      category = cat;
      break;
    }
  }
  const currentIndex = allProducts.findIndex(p => p.slug === slug);
  const prevProduct = currentIndex > 0 ? allProducts[currentIndex - 1] : null;
  const nextProduct = currentIndex < allProducts.length - 1 ? allProducts[currentIndex + 1] : null;
  const handleUseQuantity = (yards: number) => {
    const roundedYards = Math.ceil(yards);
    if (roundedYards === 0) return;
    const pn = product?.name.toLowerCase() || '';
    const light = pn.includes('bark') || pn.includes('compost') || pn.includes('topsoil') || pn.includes('50/50') || pn.includes('play chips');
    const fullLoad = light ? 15 : 12;
    if (roundedYards <= fullLoad) {
      // Single-load: just pre-select the dropdown value. The 5yd minimum is
      // enforced by the cart's billing math, so we display the actual yards
      // the user asked for (e.g. 6yd shows as 6, not 5).
      const minSingle = light ? 7 : 6;
      const snapped = Math.max(minSingle, roundedYards);
      setQuantity(String(snapped));
      setNumLoads('');
      setIsCalcOpen(false);
    } else if (product && category) {
      // Multi-load: add directly to cart using the shared splitter so the
      // last partial load is billed at the 5yd minimum (e.g. 16yd → 12 + 5).
      // This avoids the old behavior of inflating qty to fullLoad × loads.
      const lineItems = buildDeliveryLineItems(roundedYards, product.name);
      lineItems.forEach(li => {
        addToCart({
          category: category.name,
          product: li.product,
          quantity: li.quantity,
          pricePerYard: product.pricePerYard,
          requestedQuantity: li.requestedQuantity
        });
      });
      setAddedFlash(true);
      setTimeout(() => setAddedFlash(false), 2000);
      setIsCalcOpen(false);
    }
  };
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  // Pre-select quantity from estimator handoff (?qty=N). Runs after product
  // is resolved so we know whether it's light (15yd full load) or heavy (12yd).
  // - Single-load amounts (≤ fullLoad) snap to the dropdown directly.
  // - Multi-load amounts (e.g. 17yd) get stored as estimatorQty and the page
  //   adds-to-cart with the exact yardage on click, preserving the estimator's
  //   5yd-min split (12 + 5) rather than inflating to "2 full loads" (24).
  useEffect(() => {
    const qtyParam = searchParams.get('qty');
    if (!qtyParam) return;
    const n = parseInt(qtyParam, 10);
    if (isNaN(n) || n < 5) return;
    const pn = product?.name.toLowerCase() || '';
    const light = pn.includes('bark') || pn.includes('compost') || pn.includes('topsoil') || pn.includes('50/50') || pn.includes('play chips');
    const fullLoad = light ? 15 : 12;
    if (n <= fullLoad) {
      const minSingle = light ? 7 : 6;
      const snapped = Math.max(minSingle, Math.min(fullLoad, n));
      setQuantity(String(snapped));
      setNumLoads('');
      setEstimatorQty(null);
    } else {
      // Keep the exact estimator yardage. Dropdown stays at its default;
      // the Add to Truck button will use estimatorQty via buildDeliveryLineItems.
      setEstimatorQty(n);
    }
  }, [slug, searchParams, product?.name]);
  if (!product || !category) {
    return <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4" data-id="element-2689">
        <h1 className="text-2xl font-bold text-brand-charcoal dark:text-white mb-4" data-id="element-2690">
          Product not found
        </h1>
        <button onClick={() => navigate('/')} className="text-brand-orange hover:underline" data-id="element-2691">
          Return to Home
        </button>
      </div>;
  }
  const isSpecialty = category.name === 'Specialty Rock';
  const pn = product.name.toLowerCase();
  const isLight = pn.includes('bark') || pn.includes('compost') || pn.includes('topsoil') || pn.includes('50/50') || pn.includes('play chips');
  const isLimitedTo12 = product.name === 'Builders Sand';
  const handleAddToCart = () => {
    if (quantity === 'multi' && numLoads === '3+') {
      setIsQuoteModalOpen(true);
      return;
    }
    // Estimator handoff takes priority — use the exact custom yardage so the
    // cart line items match the estimator's quote (e.g. 14 → 12 + 5, not 24).
    let finalQuantity: number;
    if (estimatorQty && estimatorQty > 0) {
      finalQuantity = estimatorQty;
    } else if (quantity === 'multi') {
      const deliveries = Number(numLoads);
      const fullLoadYards = isLight ? 15 : 12;
      finalQuantity = fullLoadYards * deliveries;
    } else {
      finalQuantity = Number(quantity);
    }
    // Split into per-delivery line items so the cart shows a clean breakdown
    // (Delivery 1 / Delivery 2 / ...) and the 5yd-per-load minimum is
    // enforced on any partial last load.
    const lineItems = buildDeliveryLineItems(finalQuantity, product.name);
    lineItems.forEach(li => {
      addToCart({
        category: category.name,
        product: li.product,
        quantity: li.quantity,
        pricePerYard: product.pricePerYard,
        requestedQuantity: li.requestedQuantity
      });
    });
    setAddedFlash(true);
    setTimeout(() => setAddedFlash(false), 2000);
  };
  // Handle multi-load custom quote redirect
  useEffect(() => {
    if (quantity === 'multi' && numLoads === '3+') {
      setIsQuoteModalOpen(true);
      setNumLoads('');
      setQuantity(getDefaultQuantity(product?.name || ''));
    }
  }, [quantity, numLoads, isLight, product?.name]);
  // Reset the quantity dropdown to the per-product default whenever the
  // user navigates to a different product. React Router reuses the same
  // <Product /> component instance when only the :slug param changes, so
  // the useState lazy initializer above only runs on FIRST mount. Without
  // this effect, picking Medium Bark after viewing 1.25" Minus would
  // leave the dropdown stuck at 12 yd instead of snapping to the 15 yd
  // light-load default.
  useEffect(() => {
    if (product?.name) {
      setQuantity(getDefaultQuantity(product.name));
      setNumLoads('');
    }
    // Intentionally only re-runs on slug change — manual dropdown
    // adjustments by the customer within the same product are preserved.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);
  return <motion.main initial={{
    opacity: 0,
    y: 12
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.35,
    ease: 'easeOut'
  }} className="py-12 md:py-20 bg-[#fdfbf7] dark:bg-[#1b1b1b] min-h-[calc(100vh-64px)] transition-colors duration-300" data-id="element-2692">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2693">
        {/* Top nav: Back + Prev/Next */}
        <div className="flex items-center justify-between mb-8" data-id="element-2694">
          <Link to="/#materials" className="inline-flex items-center text-sm font-medium text-brand-muted hover:text-brand-orange transition-colors" data-id="element-2695">
            <ArrowLeft className="w-4 h-4 mr-2" data-id="element-2696" />
            Back to Materials
          </Link>
          <div className="flex items-center gap-2" data-id="element-2697">
            {prevProduct ? <Link to={`/shop/${prevProduct.slug}`} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-brand-muted dark:text-stone-400 hover:text-brand-orange dark:hover:text-brand-orange bg-white dark:bg-[#333333] border border-stone-200 dark:border-[#444444] rounded-lg transition-colors" title={prevProduct.name} data-id="element-2698">
                <ChevronLeft className="w-4 h-4" data-id="element-2699" />
                <span className="hidden sm:inline max-w-[120px] truncate" data-id="element-2700">
                  {prevProduct.name}
                </span>
                <span className="sm:hidden" data-id="element-2701">Prev</span>
              </Link> : <span className="px-3 py-1.5 text-sm text-stone-300 dark:text-stone-600 cursor-not-allowed" data-id="element-2702">
                <ChevronLeft className="w-4 h-4" data-id="element-2703" />
              </span>}
            {nextProduct ? <Link to={`/shop/${nextProduct.slug}`} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-brand-muted dark:text-stone-400 hover:text-brand-orange dark:hover:text-brand-orange bg-white dark:bg-[#333333] border border-stone-200 dark:border-[#444444] rounded-lg transition-colors" title={nextProduct.name} data-id="element-2704">
                <span className="hidden sm:inline max-w-[120px] truncate" data-id="element-2705">
                  {nextProduct.name}
                </span>
                <span className="sm:hidden" data-id="element-2706">Next</span>
                <ChevronRight className="w-4 h-4" data-id="element-2707" />
              </Link> : <span className="px-3 py-1.5 text-sm text-stone-300 dark:text-stone-600 cursor-not-allowed" data-id="element-2708">
                <ChevronRight className="w-4 h-4" data-id="element-2709" />
              </span>}
          </div>
        </div>

        <div className="bg-[#f4f7f2] dark:bg-[#333333] rounded-2xl border border-[#e6ece3] dark:border-[#444444] overflow-hidden shadow-sm flex flex-col md:flex-row" data-id="element-2710">
          {/* Image — 1:1 split with the content panel for a balanced
           hero feel where the product photo gets equal visual weight. */}
          <div className="w-full md:w-1/2 h-64 md:h-auto relative" data-id="element-2711">
            <ProductImageCarousel images={product.images || [product.image]} alt={product.name} data-id="element-2712" />
          </div>

          {/* Content */}
          <div className="w-full md:w-1/2 p-6 sm:p-8 md:p-10 flex flex-col" data-id="element-2713">
            <div className="mb-2" data-id="element-2714">
              <span className="text-xs font-bold text-brand-muted uppercase tracking-wider" data-id="element-2715">
                {category.name}
              </span>
            </div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-brand-charcoal dark:text-white mb-6" data-id="element-2716">
              {product.name}
            </h1>

            {/* Tabs */}
            <div className="flex border-b border-stone-200 dark:border-[#444] mb-6" data-id="element-2717">
              <button onClick={() => setActiveTab('overview')} className={`pb-3 px-4 text-sm font-bold transition-colors relative ${activeTab === 'overview' ? 'text-brand-orange' : 'text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200'}`} data-id="element-2718">
                Overview
                {activeTab === 'overview' && <motion.div layoutId="prodTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-orange" data-id="element-2719" />}
              </button>
              <button onClick={() => setActiveTab('specs')} className={`pb-3 px-4 text-sm font-bold transition-colors relative ${activeTab === 'specs' ? 'text-brand-orange' : 'text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200'}`} data-id="element-2720">
                Specs
                {activeTab === 'specs' && <motion.div layoutId="prodTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-orange" data-id="element-2721" />}
              </button>
              <button onClick={() => setActiveTab('docs')} className={`pb-3 px-4 text-sm font-bold transition-colors relative ${activeTab === 'docs' ? 'text-brand-orange' : 'text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200'}`} data-id="element-2722">
                Documents
                {activeTab === 'docs' && <motion.div layoutId="prodTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-orange" data-id="element-2723" />}
              </button>
            </div>

            <div className="mb-8 min-h-[200px]" data-id="element-2724">
              <AnimatePresence mode="wait" data-id="element-2725">
                {activeTab === 'overview' && <motion.div key="overview" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} data-id="element-2726">
                    <p className="text-sm text-brand-charcoal/80 dark:text-white/80 mb-6 leading-relaxed" data-id="element-2727">
                      {product.description}
                    </p>

                    {product.bestFor && product.bestFor.length > 0 && <div className="mb-6" data-id="element-2728">
                        <h3 className="text-xs font-bold text-brand-charcoal dark:text-white uppercase tracking-wider mb-3" data-id="element-2729">
                          Best For
                        </h3>
                        <div className="flex flex-wrap gap-2" data-id="element-2730">
                          {product.bestFor.map((tag, idx) => <span key={idx} className="inline-flex items-center px-3 py-1 rounded-full bg-brand-lightgray dark:bg-[#2a2a2a] text-xs font-medium text-brand-charcoal dark:text-stone-300 border border-stone-200 dark:border-[#444444]" data-id="element-2731">
                              {tag}
                            </span>)}
                        </div>
                      </div>}

                    {product.proTip && <div className="border border-[#d4ddcf] dark:border-emerald-500/20 rounded-sm p-4 flex items-start gap-3" data-id="element-2732">
                        <div className="bg-[#5d9d4e]/15 p-1.5 rounded-full shrink-0 mt-0.5" data-id="element-2733">
                          <Check className="w-4 h-4 text-[#3d7530] dark:text-[#9dd194]" data-id="element-2734" />
                        </div>
                        <div data-id="element-2735">
                          <h4 className="text-sm font-bold text-brand-charcoal dark:text-white mb-1" data-id="element-2736">
                            Pro Tip
                          </h4>
                          <p className="text-sm text-brand-charcoal/80 dark:text-stone-300 leading-relaxed" data-id="element-2737">
                            {product.proTip}
                          </p>
                        </div>
                      </div>}
                  </motion.div>}

                {activeTab === 'specs' && <motion.div key="specs" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} data-id="element-2738">
                    {product.specs ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2739">
                        {Object.entries(product.specs).map(([key, value]) => {
                    const labels: Record<string, string> = {
                      size: 'Size / Gradation',
                      weightPerYard: 'Density (Weight/Yd)',
                      compaction: 'Compaction Rating',
                      coverage: 'Est. Coverage'
                    };
                    return <div key={key} className="bg-stone-50 dark:bg-[#222222] p-4 rounded-xl border border-stone-200 dark:border-[#333333]" data-id="element-2740">
                              <p className="text-[10px] font-bold text-stone-500 dark:text-stone-400 uppercase tracking-wider mb-1.5" data-id="element-2741">
                                {labels[key] || key}
                              </p>
                              <p className="text-sm font-medium text-stone-900 dark:text-stone-200" data-id="element-2742">
                                {value}
                              </p>
                            </div>;
                  })}
                      </div> : <div className="text-center py-8 text-stone-500" data-id="element-2743">
                        No detailed specifications available for this product.
                      </div>}
                  </motion.div>}

                {activeTab === 'docs' && <motion.div key="docs" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} className="space-y-3" data-id="element-2744">
                    {/* Auto-generated Spec Sheet — real, downloadable */}
                    <button onClick={() => generateSpecSheet({
                  product,
                  categoryName: category.name
                })} className="w-full flex items-center justify-between p-4 rounded-xl border border-brand-orange/30 bg-brand-orange/5 hover:bg-brand-orange/10 hover:border-brand-orange/50 transition-all group text-left" data-id="element-2745">
                      <div className="flex items-center gap-3 min-w-0" data-id="element-2746">
                        <div className="w-10 h-10 rounded-lg bg-brand-orange/15 flex items-center justify-center shrink-0" data-id="element-2747">
                          <FileText className="w-5 h-5 text-brand-orange" data-id="element-2748" />
                        </div>
                        <div className="min-w-0" data-id="element-2749">
                          <p className="text-sm font-bold text-stone-900 dark:text-white" data-id="element-2750">
                            Product Spec Sheet
                          </p>
                          <p className="text-xs text-stone-500 dark:text-stone-400 truncate" data-id="element-2751">
                            One-page summary · specs, coverage, pricing
                          </p>
                        </div>
                      </div>
                      <span className="inline-flex items-center gap-1.5 text-xs font-bold text-brand-orange shrink-0 ml-3" data-id="element-2752">
                        <Download className="w-4 h-4" data-id="element-2753" />
                        <span className="hidden sm:inline" data-id="element-2754">Download</span>
                      </span>
                    </button>

                    {/* Need more? Email us — honest CTA for additional docs */}
                    <div className="flex items-start gap-3 p-4 rounded-xl border border-stone-200 dark:border-[#444] bg-stone-50 dark:bg-[#2a2a2a]" data-id="element-2755">
                      <div className="w-10 h-10 rounded-lg bg-stone-100 dark:bg-[#333] flex items-center justify-center shrink-0" data-id="element-2756">
                        <Mail className="w-4 h-4 text-stone-500 dark:text-stone-400" data-id="element-2757" />
                      </div>
                      <div className="min-w-0 flex-1" data-id="element-2758">
                        <p className="text-sm font-bold text-stone-900 dark:text-white mb-0.5" data-id="element-2759">
                          Need certified docs?
                        </p>
                        <p className="text-xs text-stone-500 dark:text-stone-400 leading-relaxed" data-id="element-2760">
                          Gradation curves, MSDS, and compaction test data are
                          available on request.{' '}
                          <a href={`mailto:info@253gravel.com?subject=${encodeURIComponent(`Certified docs request: ${product.name}`)}`} className="text-brand-orange font-semibold hover:underline break-all" data-id="element-2761">
                            info@253gravel.com
                          </a>
                          .
                        </p>
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            <div className="mt-auto" data-id="element-2762">
              {isSpecialty ? <div className="space-y-4" data-id="element-2763">
                  <div className="p-4 bg-brand-lightgray/50 dark:bg-[#222222] rounded-xl border border-stone-200 dark:border-[#444444]" data-id="element-2764">
                    <p className="text-sm text-brand-charcoal dark:text-stone-300" data-id="element-2765">
                      Pricing and availability for specialty rock varies. Please
                      contact us directly for a custom quote.
                    </p>
                  </div>
                  <a href="tel:206-619-3742" className="w-full bg-brand-charcoal dark:bg-white text-white dark:text-brand-charcoal hover:bg-brand-charcoal/90 dark:hover:bg-stone-200 py-4 rounded-xl font-bold text-lg transition-colors flex items-center justify-center" data-id="element-2766">
                    <Phone className="w-5 h-5 mr-2" data-id="element-2767" />
                    Call for Pricing
                  </a>
                </div> : <div className="space-y-6" data-id="element-2768">
                  {(() => {
                const fullLoad = isLight ? 15 : 12;
                const smallLoadThreshold = isLight ? 12 : 10;
                const MIN = 3;
                const requestedYards = estimatorQty ? estimatorQty : quantity === 'multi' && numLoads && numLoads !== '3+' ? Number(numLoads) * fullLoad : quantity !== 'multi' ? Number(quantity) : 0;
                const loads: number[] = [];
                if (requestedYards > 0) {
                  if (requestedYards <= fullLoad) {
                    loads.push(Math.max(MIN, requestedYards));
                  } else {
                    const count = Math.ceil(requestedYards / fullLoad);
                    const last = Math.max(MIN, requestedYards - (count - 1) * fullLoad);
                    for (let i = 0; i < count - 1; i++) loads.push(fullLoad);
                    loads.push(last);
                  }
                }
                const billableYards = loads.reduce((a, b) => a + b, 0);
                const bulkPct = billableYards >= fullLoad * 4 ? 0.1 : billableYards >= (isLight ? fullLoad : fullLoad * 2) ? 0.05 : 0;
                let totalCost = 0;
                let hasSurchargedLoad = false;
                for (const ld of loads) {
                  let sur = 1;
                  if (ld < smallLoadThreshold) {
                    sur = 1 + (smallLoadThreshold - ld) * 0.1;
                  }
                  if (sur > 1) hasSurchargedLoad = true;
                  totalCost += Math.ceil(product.pricePerYard * sur) * ld;
                }
                if (bulkPct > 0) {
                  totalCost = Math.round(totalCost * (1 - bulkPct));
                }
                const effectiveAvg = billableYards > 0 ? Math.round(totalCost / billableYards) : product.pricePerYard;
                const showsEffective = billableYards > 0 && (hasSurchargedLoad || bulkPct > 0) && effectiveAvg !== product.pricePerYard;
                return <div className="flex items-start justify-between gap-4 border-b border-stone-100 dark:border-[#444444] pb-6" data-id="element-2769">
                        {/* Left: price column */}
                        <div className="min-w-0 flex-1" data-id="element-2770">
                          <p className="text-sm text-brand-muted dark:text-[#6FB389] font-medium mb-1" data-id="element-2771">
                            Price
                          </p>
                          <p className="text-3xl font-heading font-bold text-brand-charcoal dark:text-white" data-id="element-2772">
                            $
                            {showsEffective ? effectiveAvg : product.pricePerYard}{' '}
                            <span className="text-base font-normal text-brand-muted dark:text-[#6FB389]" data-id="element-2773">
                              / yard{showsEffective ? ' avg' : ''}
                            </span>
                            {bulkPct > 0 && <span className="inline-flex items-center text-xs font-bold text-stone-900 bg-amber-300 dark:bg-amber-400 px-2 py-0.5 rounded-md ml-2 align-middle uppercase tracking-wide" data-id="element-2774">
                                {Math.round(bulkPct * 100)}% Bulk Discount
                              </span>}
                          </p>
                          {showsEffective && hasSurchargedLoad && !bulkPct && <p className="text-xs text-brand-muted dark:text-stone-400 mt-1.5 leading-relaxed" data-id="element-2775">
                              <span className="line-through" data-id="element-2776">
                                ${product.pricePerYard}/yd
                              </span>
                              <span className="text-amber-700 dark:text-amber-400 ml-2 font-semibold" data-id="element-2777">
                                small-load surcharge applies
                              </span>
                              <span className="block mt-0.5" data-id="element-2778">
                                Base ${product.pricePerYard}/yd at{' '}
                                {smallLoadThreshold}+ yards per delivery.
                              </span>
                            </p>}
                          {showsEffective && bulkPct > 0 && <p className="text-sm text-stone-700 dark:text-stone-300 mt-1" data-id="element-2779">
                              <span className="line-through" data-id="element-2780">
                                ${product.pricePerYard}/yd
                              </span>
                              <span className="text-amber-600 dark:text-amber-400 ml-2 font-semibold" data-id="element-2781">
                                Save $
                                {product.pricePerYard * billableYards - totalCost}{' '}
                                on {billableYards} yards
                              </span>
                            </p>}
                          {!showsEffective && billableYards === 0 && <p className="text-xs text-brand-muted dark:text-stone-400 mt-1.5" data-id="element-2782">
                              Base price at {smallLoadThreshold}+ yards per
                              delivery. Smaller loads include a surcharge.
                            </p>}
                        </div>
                        {/* Right: materials subtotal — mirrors the
                         "$87 / yard" pattern on the left by putting the
                         small qualifier ("12 yds /") IN FRONT of the
                         big number ("$1,044"). The leading-blank top
                         row keeps the big numbers baseline-aligned
                         with the Price column even though the right
                         column has no top label. */}
                        {billableYards > 0 && <div className="flex-1 min-w-0 text-right" data-id="element-2783">
                            {/* invisible spacer that matches the
                             Price label's height so both columns'
                             big numbers land on the same baseline */}
                            <p aria-hidden="true" className="text-sm font-medium mb-1 select-none" data-id="element-2784">
                              &nbsp;
                            </p>
                            <p className="text-3xl font-heading font-bold text-brand-charcoal dark:text-white whitespace-nowrap" data-id="element-2785">
                              <span className="text-base font-normal text-brand-muted dark:text-[#6FB389]" data-id="element-2786">
                                {billableYards} yds /
                              </span>{' '}
                              ${totalCost.toLocaleString()}
                            </p>
                            <p className="text-xs text-brand-muted dark:text-[#6FB389] mt-0.5" data-id="element-2787">
                              + delivery
                            </p>
                          </div>}
                      </div>;
              })()}

                  {/* Estimator handoff banner — appears when the user
                   arrived from the Instant Estimator with a custom yardage
                   that needs to split across multiple deliveries. Without
                   this chip, the dropdown still shows its default ("12 Yards")
                   and the user thinks the page ignored their calculated
                   amount. Clicking "Change quantity" reverts to the dropdown. */}
                  {estimatorQty && estimatorQty > 0 && (() => {
                const loads = (() => {
                  const fullLoad = isLight ? 15 : 12;
                  const MIN = 5;
                  const n = estimatorQty;
                  if (n <= fullLoad) return [Math.max(MIN, n)];
                  const count = Math.ceil(n / fullLoad);
                  const last = n - (count - 1) * fullLoad;
                  const billableLast = Math.max(MIN, last);
                  const arr: number[] = [];
                  for (let i = 0; i < count - 1; i++) arr.push(fullLoad);
                  arr.push(billableLast);
                  return arr;
                })();
                const breakdown = loads.join(' + ');
                const totalBillable = loads.reduce((a, b) => a + b, 0);
                return <motion.div initial={{
                  opacity: 0,
                  y: -6
                }} animate={{
                  opacity: 1,
                  y: 0
                }} className="rounded-xl border border-[#059F40]/30 bg-[#059F40]/10 p-3 sm:p-4" data-id="element-2788">
                          <div className="flex items-start gap-3" data-id="element-2789">
                            <div className="w-8 h-8 rounded-full bg-[#059F40]/20 flex items-center justify-center shrink-0" data-id="element-2790">
                              <Check className="w-4 h-4 text-[#059F40]" data-id="element-2791" />
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-2792">
                              <p className="text-sm font-bold text-brand-charcoal dark:text-white" data-id="element-2793">
                                From your estimator: {estimatorQty}{' '}
                                {estimatorQty === 1 ? 'yard' : 'yards'}
                              </p>
                              <p className="text-xs text-brand-charcoal/70 dark:text-stone-300 mt-0.5" data-id="element-2794">
                                Splits into {loads.length}{' '}
                                {loads.length === 1 ? 'delivery' : 'deliveries'}{' '}
                                ({breakdown} yd)
                                {totalBillable !== estimatorQty && <span className="text-amber-700 dark:text-amber-400" data-id="element-2795">
                                    {' '}
                                    · billed as {totalBillable} yd (3 yd order
                                    min)
                                  </span>}
                              </p>
                            </div>
                            <button type="button" onClick={() => setEstimatorQty(null)} className="shrink-0 text-xs font-semibold text-brand-charcoal/70 dark:text-stone-400 hover:text-brand-orange dark:hover:text-brand-orange underline underline-offset-2" data-id="element-2796">
                              Change
                            </button>
                          </div>
                        </motion.div>;
              })()}

                  <div className="space-y-4" data-id="element-2797">
                    <div className="flex items-center justify-between gap-3 flex-wrap" data-id="element-2798">
                      <label className="block text-sm font-bold text-brand-charcoal dark:text-stone-300" data-id="element-2799">
                        {estimatorQty ? 'Or change quantity' : 'Select Quantity'}
                      </label>
                      {(() => {
                    const fullLoad = isLight ? 15 : 12;
                    const tier2Yards = isLight ? fullLoad : fullLoad * 2;
                    const tier3Yards = fullLoad * 4;
                    return <span className="text-[11px] text-brand-muted dark:text-stone-400" data-id="element-2800">
                            5% at {tier2Yards} yd ·{' '}
                            <span className="font-semibold text-[#AEB9B2] dark:text-[#6FB389]" data-id="element-2801">
                              10% at {tier3Yards} yd
                            </span>
                          </span>;
                  })()}
                    </div>
                    {/* Quantity grid sits on its own row, full-width;
                     Add to Truck stacks below as a full-width button. */}
                    <div className="flex flex-col gap-4" data-id="element-2802">
                      <div className="w-full" data-id="element-2803">
                        {/* Inline pill grid — replaces the previous dropdown
                         so the menu can never overlap the Pro Tip above.
                         Two hero pills (Multiple loads + Full Load · Most
                         picked) sit on top as full-width rows; the
                         remaining yardages fall into a responsive 3-col
                         grid below. Active state uses a clearly brighter
                         mint than the Pro Tip wash, so the two surfaces
                         read as distinct UI layers. */}
                        {(() => {
                      const fullLoad = isLight ? 15 : 12;
                      // Light materials (topsoil & organics) get 3yd
                      // increments — matches the Hero estimator and
                      // Services hub. Heavy materials keep 1yd steps
                      // for finer-grained driveway/patio sizing.
                      // Reversed so the LARGEST yardages (11/10/9 for
                      // heavy, 12/9/6 for light) sit on the row directly
                      // above the Full Load split — reading order flows
                      // smaller → larger → Full Load, which mirrors the
                      // most common upgrade path.
                      const lessYards = isLight ? [3, 6, 9, 12] : [3, 4, 5, 6, 7, 8, 9, 10, 11];
                      // Brighter, distinctly minty active state so the
                      // selected pill doesn't blend into the sage Pro
                      // Tip surface above. Inactive pills sit on plain
                      // white/dark-card so the active pill pops.
                      // Active pill greens tuned to harmonize with
                      // the Pro Tip sage wash above (#e8ede4 / #d4ddcf).
                      // Same olive-sage family, one shade brighter +
                      // saturated so the selected pill still reads as
                      // "chosen" without competing with the Pro Tip.
                      const activeCls = 'bg-[#eaf2e2] dark:bg-[#1d3324] border-[#a8c498] dark:border-[#4a7a4f] text-[#3d7530] dark:text-[#9dd194] font-bold shadow-sm';
                      // Inactive pills share the Pro Tip's olive-sage
                      // wash so the whole quantity surface reads as
                      // one cohesive sage card. Active state shifts
                      // to a brighter, more saturated green + bold
                      // text so the selected pill still pops clearly
                      // against the matching inactive background.
                      const inactiveCls = 'bg-[#e8ede4] dark:bg-emerald-500/[0.07] border-[#d4ddcf] dark:border-emerald-500/20 text-brand-charcoal dark:text-stone-200 hover:bg-[#dde5d6] dark:hover:bg-emerald-500/[0.12] hover:border-[#bccab2] dark:hover:border-emerald-500/30';
                      return <div className="space-y-2" data-id="element-2804">
                              {/* Smaller yardages — compact 3-col grid, 11
                               down to 3. Uniform size: no sublabels, all
                               pills are a single line so heights match. */}
                              <div className="grid grid-cols-3 gap-2" data-id="element-2805">
                                {lessYards.map(y => <button key={y} type="button" onClick={() => {
                            setQuantity(String(y));
                            setNumLoads('');
                          }} className={`px-2 py-2 rounded-none border text-sm font-medium transition-all text-center ${quantity === String(y) ? activeCls : inactiveCls}`} title={y === 3 ? '3 yd · Order minimum' : `${y} yd`} data-id="element-2806">
                                    {y} yd
                                  </button>)}
                              </div>
                              {/* Hero split row — Full Load + Multiple loads
                               share one line directly above Add to Truck.
                               Full Load is the most-picked default; Multi
                               sits next to it for the bigger-project path. */}
                              <div className="grid grid-cols-2 gap-2" data-id="element-2807">
                                <button type="button" onClick={() => {
                            setQuantity(String(fullLoad));
                            setNumLoads('');
                          }} className={`w-full px-3 py-2 rounded-none border text-sm font-medium transition-all text-center ${quantity === String(fullLoad) ? activeCls : inactiveCls}`} data-id="element-2808">
                                  Full Load · {fullLoad} yd
                                  {/* "Most picked" hides on narrow mobile
                                   widths where it forces a second line —
                                   appears inline only when the pill has
                                   room for it. */}
                                  <span className={`hidden sm:inline ml-1 text-xs font-normal ${quantity === String(fullLoad) ? 'text-[#3d7530] dark:text-[#9dd194]' : 'text-brand-muted'}`} data-id="element-2809">
                                    · Most picked
                                  </span>
                                </button>
                                <button type="button" onClick={() => setQuantity('multi')} className={`w-full px-3 py-2 rounded-none border text-sm font-medium transition-all text-center ${quantity === 'multi' ? activeCls : inactiveCls}`} data-id="element-2810">
                                  Multiple · {fullLoad * 2}+ yd
                                </button>
                              </div>
                            </div>;
                    })()}

                        <AnimatePresence data-id="element-2811">
                          {quantity === 'multi' && <motion.div initial={{
                        opacity: 0
                      }} animate={{
                        opacity: 1
                      }} exit={{
                        opacity: 0
                      }} transition={{
                        duration: 0.2
                      }} className="overflow-visible mt-3" data-id="element-2812">
                              <CustomSelect value={numLoads} onChange={val => setNumLoads(val)} placeholder="How many loads?" options={[{
                          value: '2',
                          label: `2 Loads (${isLight ? '30' : '24'} yds)`
                        }, {
                          value: '3',
                          label: `3 Loads (${isLight ? '45' : '36'} yds)`
                        }, {
                          value: '4',
                          label: `4 Loads (${isLight ? '60' : '48'} yds)`
                        }, {
                          value: '3+',
                          label: '5+ Loads (Custom Quote)'
                        }]} data-id="element-2813" />
                            </motion.div>}
                        </AnimatePresence>
                      </div>

                      {addedFlash ? <motion.div initial={{
                    opacity: 0,
                    scale: 0.95
                  }} animate={{
                    opacity: 1,
                    scale: 1
                  }} className="w-full flex items-center justify-center bg-[#76cc26]/10 dark:bg-[#76cc26]/10 text-[#76cc26] dark:text-[#76cc26] rounded-none font-bold border border-[#76cc26]/30 dark:border-[#76cc26]/30 py-4 text-lg" data-id="element-2814">
                          <Check className="w-5 h-5 mr-2" data-id="element-2815" />
                          Added to Truck
                        </motion.div> : <button onClick={handleAddToCart} disabled={quantity === 'multi' && !numLoads} className={`w-full py-4 rounded-none font-bold text-lg transition-all duration-300 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed ${quantity && quantity !== 'multi' ? 'bg-brand-button hover:bg-brand-buttonHover text-white shadow-[0_0_12px_rgba(5,137,77,0.4)]' : quantity === 'multi' && numLoads && numLoads !== '3+' ? 'bg-brand-button hover:bg-brand-buttonHover text-white shadow-[0_0_12px_rgba(5,137,77,0.4)]' : 'bg-brand-charcoal dark:bg-white/10 text-white dark:text-white hover:bg-brand-charcoal/90 dark:hover:bg-white/15 border border-transparent dark:border-white/10'}`} data-id="element-2816">
                          <Truck className="w-5 h-5 mr-2" data-id="element-2817" />
                          Add to Truck
                        </button>}
                    </div>

                    <div className="flex justify-center" data-id="element-2818">
                      <SameDayBadge variant="full" data-id="element-2819" />
                    </div>

                    {/* Material sanity check — helps the customer confirm
                     the product they're viewing matches their project
                     BEFORE adding to cart. Lives here (not in the cart
                     drawer) so the decision happens with intent fresh.
                     The helper is now category-aware: each category in
                     data/products.ts ships its own pair of questions
                     and its own recommendation table inside
                     components/CartHelper.tsx, so the suggestions stay
                     relevant whether the customer is browsing base rock,
                     clear & drain rock, soil/mulch, quarry rip-rap, or
                     decorative landscape stone. */}
                    <CartHelper contextProduct={{
                  slug: product.slug,
                  name: product.name
                }} data-id="element-2820" />

                    {/* Inline Coverage Calculator — pulldown style, matches
                     the materials hub (Services) calculator. Replaces the
                     old fixed side drawer so the experience is identical
                     whether the customer arrives via the hub or a direct
                     /shop/:slug link. Reuses the existing calculator
                     state (shape/length/width/diameter/base/height/depth
                     /calcYards/calcTons) and `handleUseQuantity()`. */}
                    <div className="mt-3 border-t border-brand-charcoal/10 dark:border-white/10 pt-3" data-id="element-2821">
                      <button onClick={() => setIsCalcOpen(v => !v)} className="w-full flex items-center justify-center gap-2 py-2 text-sm font-medium text-brand-orange hover:text-brand-orangeHover transition-colors" aria-expanded={isCalcOpen} data-id="element-2822">
                        <CalcIcon className="w-4 h-4" data-id="element-2823" />
                        {isCalcOpen ? 'Hide Calculator' : 'Calculate how much I need'}
                      </button>
                      <AnimatePresence data-id="element-2824">
                        {isCalcOpen && <motion.div initial={{
                      opacity: 0,
                      height: 0
                    }} animate={{
                      opacity: 1,
                      height: 'auto'
                    }} exit={{
                      opacity: 0,
                      height: 0
                    }} transition={{
                      duration: 0.2
                    }} className="overflow-hidden" data-id="element-2825">
                            <div className="mt-4" data-id="element-2826">
                              <SimpleAreaCalculator materialSlug={product.slug} onApply={handleUseQuantity} data-id="element-2827" />
                            </div>
                          </motion.div>}
                      </AnimatePresence>
                    </div>

                    <AnimatePresence data-id="element-2828">
                      {quantity === 'multi' && numLoads && numLoads !== '3+' && <motion.div initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} exit={{
                    opacity: 0,
                    height: 0
                  }} className="overflow-hidden" data-id="element-2829">
                            <div className="flex items-start gap-2 mt-3 p-3 bg-brand-orange/10 border border-brand-orange/20 rounded-lg" data-id="element-2830">
                              <Info className="w-4 h-4 text-brand-orange mt-0.5 shrink-0" data-id="element-2831" />
                              <p className="text-xs text-brand-charcoal dark:text-stone-300" data-id="element-2832">
                                Multi-load orders require separate deliveries.
                                You will be charged a delivery fee per load.
                              </p>
                            </div>
                          </motion.div>}
                    </AnimatePresence>

                    {quantity === 'multi' && !numLoads && <div className="flex items-start gap-2 mt-3 p-3 bg-brand-orange/10 border border-brand-orange/20 rounded-lg" data-id="element-2833">
                        <Info className="w-4 h-4 text-brand-orange mt-0.5 shrink-0" data-id="element-2834" />
                        <p className="text-xs text-brand-charcoal dark:text-stone-300" data-id="element-2835">
                          Please select how many loads you need.
                        </p>
                      </div>}
                  </div>

                  {quantity === 'multi' && numLoads === '3+' && <button onClick={() => setIsQuoteModalOpen(true)} className="w-full bg-brand-button hover:bg-brand-buttonHover text-white py-4 rounded-none font-bold text-lg transition-colors flex items-center justify-center" data-id="element-2836">
                      Get a Custom Quote
                    </button>}

                  {/* "Need a custom amount? Call us..." line removed —
                   customers can dial in any 3-yd-stepped amount via
                   the quantity dropdown and run the inline calculator
                   above for project-based sizing.
                   "Quiz & Calculator" pill also removed — the inline
                   calculator above and the in-context CartHelper cover
                   both of those jobs without bouncing the user away. */}
                  <div className="flex flex-wrap items-center justify-center gap-2 pt-3 mt-1 border-t border-brand-charcoal/10 dark:border-white/10" data-id="element-2837">
                    <Link to={`/compare?add=${encodeURIComponent(product.slug)}`} className="text-xs font-medium text-brand-charcoal/80 dark:text-white/80 bg-brand-charcoal/5 dark:bg-white/5 hover:bg-brand-orange/15 hover:text-brand-orange dark:hover:text-brand-orange border border-brand-charcoal/10 dark:border-white/10 hover:border-brand-orange/40 rounded-full px-3 py-1.5 transition-colors" data-id="element-2838">
                      Compare Materials
                    </Link>
                    <Link to="/diy-help" className="text-xs font-medium text-brand-charcoal/80 dark:text-white/80 bg-brand-charcoal/5 dark:bg-white/5 hover:bg-brand-orange/15 hover:text-brand-orange dark:hover:text-brand-orange border border-brand-charcoal/10 dark:border-white/10 hover:border-brand-orange/40 rounded-full px-3 py-1.5 transition-colors" data-id="element-2839">
                      DIY Help
                    </Link>
                  </div>
                </div>}
            </div>
          </div>
        </div>
      </div>

      <QuoteModal isOpen={isQuoteModalOpen} onClose={() => setIsQuoteModalOpen(false)} data-id="element-2840" />

      {/* Old fixed-position Calculator Drawer removed — replaced by the
                                                                                                  inline "Calculate how much I need" pulldown under the Add to Truck
                                                                                                  button, matching the Services hub product detail layout. */}
    </motion.main>;
}